import { Stack } from '@mui/material';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import texts from './texts';
import { NationalIdModel, NationalIdSchema } from './validation';
import { TextField } from '@/ui/kit/components/textfield';
import LoadingButton from '@/components/LoadingButton';
import FormTitle from '@/containers/auth/components/FormTitle';
import { useCheckRegister } from '@/api/hooks/auth/useCheckRegister';
import { ROUTES, SESSION_KEYS } from '@/utils/constants';
import { CheckRegister, SendOtp } from '@/api/hooks/auth/types';
import { useSendOtp } from '@/api/hooks/auth/useSendOtp';
import { get2MinuetsLaterInTimestamp } from '@/utils/function';
import { sendGoogleGTMEvent } from '@/utils/googleGTM';
const NationalIdFrom = () => {
  const router = useRouter();
  const {
    control,
    handleSubmit
  } = useForm<NationalIdModel>({
    resolver: zodResolver(NationalIdSchema),
    mode: 'onSubmit'
  });
  const sendOtpSuccessHandler = (data: SendOtp['Response']) => {
    sessionStorage.setItem(SESSION_KEYS.auth.obfuscationMobileNumber, data.mobile_number);
    sessionStorage.setItem(SESSION_KEYS.auth.otpTimeout, get2MinuetsLaterInTimestamp().toString());
    router.push(ROUTES.otpVerification.href);
  };
  const {
    mutate: mutateSendOtp
  } = useSendOtp({
    onSuccess: sendOtpSuccessHandler
  });
  const checkRegisterSuccessHandler = (data: CheckRegister['Response'], params: CheckRegister['Payload']) => {
    sessionStorage.removeItem(SESSION_KEYS.auth.nationalId);
    sessionStorage.setItem(SESSION_KEYS.auth.userType, 'legal');
    sessionStorage.setItem(SESSION_KEYS.auth.companyNationalId, params.company_national_id);
    sessionStorage.setItem(SESSION_KEYS.auth.accountsCount, `${data.accounts_count}`);
    sessionStorage.setItem(SESSION_KEYS.auth.obfuscationMobileNumber, data.mobile_number);
    if (data.accounts_count > 0) {
      mutateSendOtp({
        type: 'legal',
        company_national_id: params.company_national_id
      });
    } else {
      router.push(ROUTES.register.href);
    }
  };
  const {
    mutate: mutateCheckRegister,
    isPending
  } = useCheckRegister({
    onSuccess: checkRegisterSuccessHandler
  });
  const onSubmit: SubmitHandler<NationalIdModel> = (data: NationalIdModel) => {
    sendGoogleGTMEvent({
      event: 'log_reg_checkotp'
    });
    mutateCheckRegister({
      ...data,
      type: 'legal'
    });
  };
  return <Stack component='form' width='100%' gap='var(--spacing_l)' onSubmit={handleSubmit(onSubmit)} noValidate data-sentry-element="Stack" data-sentry-component="NationalIdFrom" data-sentry-source-file="index.tsx">
      <FormTitle title={texts.title} data-sentry-element="FormTitle" data-sentry-source-file="index.tsx" />

      <Controller control={control} name='company_national_id' render={({
      field,
      fieldState
    }) => <NumberFormat type='tel' customInput={TextField} name={field.name} disabled={isPending} label={texts.form.nationalCode.label} placeholder={texts.form.nationalCode.placeholder} error={fieldState.invalid} helperText={fieldState.error?.message} value={field.value ?? ''} onChange={field.onChange} inputProps={{
      maxLength: 11
    }} size='large' sx={{
      textAlign: 'left',
      direction: 'ltr'
    }} customNumerals={['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']} isNumericString />} data-sentry-element="Controller" data-sentry-source-file="index.tsx" />

      <LoadingButton type='submit' variant='contained' size='large' loading={isPending} disabled={isPending} data-sentry-element="LoadingButton" data-sentry-source-file="index.tsx">
        {texts.form.button}
      </LoadingButton>
    </Stack>;
};
export default NationalIdFrom;